'use client'
import { useEffect } from 'react'

import { create } from 'zustand/react'

import { pick } from '@zag-js/utils'

import { DEFAULT_REGION } from '~/utils/consts'
import { COOKIE, getCookieAsParsedJson, setCookie } from '~/utils/cookies'
import { getClosestRegionByGeolocation } from '~/utils/getClosestRegionByGeolocation'
import { getLogger } from '~/utils/logger'

import { Region, useFestivalRegionsQuery, useRegionsQuery } from '~/app/generated/graphql'

type LeanRegion = Omit<Region, 'zones'>

type SelectedRegion = {
  id: string
  name: string
}

type LocationCookie = SelectedRegion & { isProposed: boolean }

interface UseLocation {
  loading: boolean
  selectedRegion: SelectedRegion | null
  regions: LeanRegion[]
  setRegion(region: LeanRegion): void
  getRegion(id: string): LeanRegion | undefined
}

export const locationLogger = getLogger('location', 'debug')

export const useSelectedRegion = create<{
  region: SelectedRegion | null
  setRegion: (region: SelectedRegion) => void
  init: (regions: Omit<Region, 'zones'>[]) => void
  isInitialized: boolean
}>((setState, getState) => ({
  region: DEFAULT_REGION,
  setRegion: (region: SelectedRegion) => {
    const selectedRegion = pick(region, ['id', 'name'])
    setState(_ => ({ region: selectedRegion }))
    setCookie(COOKIE.LOCATION, JSON.stringify(selectedRegion))
  },
  isInitialized: false,
  init: async regions => {
    if (getState().isInitialized) return

    const locationCookie = getCookieAsParsedJson(COOKIE.LOCATION) as LocationCookie

    const isLocationCookieValid = !!locationCookie && regions.map(r => r.id).includes(locationCookie.id)

    if (isLocationCookieValid) {
      getState().setRegion(pick(locationCookie, ['id', 'name']))
    } else {
      const closestRegion = await getClosestRegionByGeolocation(regions)

      getState().setRegion(closestRegion)
      locationLogger(`Set region by geolocation, set selected region id: ${closestRegion.id}`)
    }

    setState(() => ({ isInitialized: true }))
  },
}))

export const useLocation = (festivalEditionId?: string): UseLocation => {
  const selectedRegion = useSelectedRegion()

  const { data: defaultRegions, loading: defaultRegionsLoading } = useRegionsQuery({
    skip: !!festivalEditionId,
    nextFetchPolicy: 'cache-first',
  })

  const { data: festivalRegions, loading: festivalRegionsLoading } = useFestivalRegionsQuery({
    skip: !festivalEditionId,
    nextFetchPolicy: 'cache-first',
    variables: { festivalEditionId: festivalEditionId ?? '' },
  })

  const dataRegions = festivalEditionId ? festivalRegions : defaultRegions
  const loading = festivalEditionId ? festivalRegionsLoading : defaultRegionsLoading

  const regions = dataRegions?.regions

  const getRegion = (id: string) => {
    return (regions || []).find(r => r.id === id)
  }

  useEffect(() => {
    if (!regions) return

    selectedRegion.init(regions)
  }, [regions, selectedRegion])

  return {
    regions: regions || [],
    loading,
    selectedRegion: selectedRegion.region,
    setRegion: selectedRegion.setRegion,
    getRegion,
  }
}
